import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { database, auth } from './firebase';
import { ref, onValue, push, set, remove } from 'firebase/database';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { uploadImage } from './cloudinary';
import { FaTrash, FaEdit, FaGithub, FaExternalLinkAlt } from 'react-icons/fa';
import AdminNavbar from './components/AdminNavbar';

const Container = styled.div`
  max-width: 1300px;
  margin: 5rem auto 3rem auto;
  padding: 1rem;
  height: 100dvh;
`;

const Title = styled.h2`
  font-size: 2rem;
  margin-bottom: 2rem;
  color: #333;
  margin-left: 1rem;
`;

const ContentWrapper = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: 2rem;
  margin-top: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormWrapper = styled.div`
  background: white;
  padding: 0rem;
  border-radius: 15px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  height: fit-content;
  top: 2rem;
  max-width: 600px;
  position: relative;
  top: -5rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    top: 0rem;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background: white;
  padding: 2rem 3rem;
  border-radius: 10px;
  box-shadow: 0 0 10px rgba(0,0,0,0.1);

  @media (max-width: 768px) {
    padding: 1rem 1rem;
  }
`;

const FormHeading = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #333;
  text-align: center;
  font-weight: 400;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 1rem;
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 1rem;
  min-height: 100px;
`;

const FileInput = styled.input`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 1rem;
  width: 100%;
`;

const Button = styled.button`
  padding: 0.75rem;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 1rem;

  &:hover {
    background: #0056b3;
  }
`;

// const DeleteButton = styled(Button)`
//   background: #dc3545;
//   &:hover {
//     background: #c82333;
//   }
// `;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  max-width: 1000px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ProjectImage = styled.img`
  width: 100%;
  height: 120px;
  object-fit: cover;
  border-radius: 8px;
  margin-bottom: 0.5rem;
  transition: transform 0.3s ease;
`;

const ProjectCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.25rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.05);
  min-width: 300px;
  height: fit-content;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 15px rgba(0, 0, 0, 0.1);
    
    &::before {
      opacity: 1;
    }

    ${ProjectImage} {
      transform: scale(1.05);
    }
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #007bff, #00bcd4);
    opacity: 0.7;
    transition: opacity 0.3s ease;
  }
`;

const ProjectTitle = styled.h3`
  font-size: 1.1rem;
  margin: 0;
  color: #333;
  font-weight: 600;
`;

const ProjectDescription = styled.p`
  color: #555;
  line-height: 1.4;
  margin: 0;
  font-size: 0.85rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ProjectTech = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.5rem;
`;

const TechTag = styled.span`
  background: #f0f0f0;
  color: #666;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  transition: all 0.3s ease;

  ${ProjectCard}:hover & {
    background: #e3f2fd;
    color: #007bff;
  }
`;

const ProjectLinks = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 0.5rem;
`;

const ProjectLink = styled.a`
  color: #007bff;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  transition: all 0.3s ease;
  font-size: 0.85rem;

  &:hover {
    color: #0056b3;
    text-decoration: underline;
  }

  svg {
    font-size: 0.9rem;
  }
`;

const ProjectActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding-top: 0.5rem;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 0.35rem;
  border-radius: 5px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.85rem;

  &:hover {
    background: #f0f0f0;
    color: #007bff;
  }

  svg {
    font-size: 0.9rem;
  }
`;

const FormLabel = styled.label`
  font-size: 0.9rem;
  color: #555;
  margin-bottom: 0.25rem;
  display: block;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Projects = () => {
  const [projects, setProjects] = useState([]);
  // const [loading, setLoading] = useState(true);
  // const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    technologies: '',
    imageUrl: '',
    githubUrl: '',
    demoUrl: ''
  });
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const projectsRef = ref(database, 'projects');
    onValue(projectsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const projectsList = Object.entries(data).map(([id, project]) => ({
          id,
          ...project
        }));
        setProjects(projectsList);
      }
    });
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      setFormData(prev => ({
        ...prev,
        image: e.target.files[0]
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.image) {
      alert('Please select an image');
      return;
    }

    setUploading(true);
    try {
      console.log('Starting project creation process...');
      
      // Upload image to ImgBB
      console.log('Uploading image...');
      const imageUrl = await uploadImage(formData.image);
      console.log('Image uploaded successfully, URL:', imageUrl);

      // Save project data with image URL
      console.log('Saving project data...');
      const projectsRef = ref(database, 'projects');
      const newProjectRef = push(projectsRef);
      await set(newProjectRef, {
        title: formData.title,
        description: formData.description,
        technologies: formData.technologies.split(',').map(tech => tech.trim()),
        githubUrl: formData.githubUrl,
        demoUrl: formData.demoUrl,
        imageUrl: imageUrl,
        createdAt: new Date().toISOString()
      });
      console.log('Project saved successfully');

      // Reset form
      setFormData({
        title: '',
        description: '',
        technologies: '',
        githubUrl: '',
        demoUrl: '',
        image: null
      });
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      alert(`Error adding project: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const projectRef = ref(database, `projects/${id}`);
      await remove(projectRef);
    } catch (error) {
      console.error('Error deleting project:', error);
    }
  };

  // const handleLogout = async () => {
  //   try {
  //     await signOut(auth);
  //     navigate('/admin');
  //   } catch (error) {
  //     console.error('Error logging out:', error);
  //   }
  // };

  return (
    <Container>
      <AdminNavbar />
      
      <Title>Projects</Title>
      <ContentWrapper>
      <ProjectsGrid>
          {projects.map((project) => (
            <ProjectCard key={project.id}>
              <ProjectImage 
                src={project.imageUrl || 'https://via.placeholder.com/400x200'} 
                alt={project.title}
              />
              <ProjectTitle>{project.title}</ProjectTitle>
              <ProjectDescription>{project.description}</ProjectDescription>
              <ProjectTech>
                {project.technologies?.map((tech, index) => (
                  <TechTag key={index}>{tech}</TechTag>
                ))}
              </ProjectTech>
              <ProjectLinks>
                {project.githubUrl && (
                  <ProjectLink href={project.githubUrl} target="_blank" rel="noopener noreferrer">
                    <FaGithub /> GitHub
                  </ProjectLink>
                )}
                {project.demoUrl && (
                  <ProjectLink href={project.demoUrl} target="_blank" rel="noopener noreferrer">
                    <FaExternalLinkAlt /> Demo
                  </ProjectLink>
                )}
              </ProjectLinks>
              <ProjectActions>
                <ActionButton onClick={() => setEditingProject(project)}>
                  <FaEdit /> Edit
                </ActionButton>
                <ActionButton onClick={() => handleDelete(project.id)}>
                  <FaTrash /> Delete
                </ActionButton>
              </ProjectActions>
            </ProjectCard>
          ))}
        </ProjectsGrid>
        <FormWrapper>
          <Form onSubmit={handleSubmit}>
            <FormHeading>Add New Project</FormHeading>
            <FormGroup>
              <FormLabel htmlFor="title">Project Title</FormLabel>
              <Input
                id="title"
                type="text"
                name="title"
                placeholder="Enter project title"
                value={formData.title}
                onChange={handleChange}
                required
              />
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="description">Description</FormLabel>
              <TextArea
                id="description"
                name="description"
                placeholder="Enter project description"
                value={formData.description}
                onChange={handleChange}
                required
              />
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="technologies">Technologies</FormLabel>
              <Input
                id="technologies"
                type="text"
                name="technologies"
                placeholder="Enter technologies (comma separated)"
                value={formData.technologies}
                onChange={handleChange}
              />
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="githubUrl">GitHub URL</FormLabel>
              <Input
                id="githubUrl"
                type="text"
                name="githubUrl"
                placeholder="Enter GitHub repository URL"
                value={formData.githubUrl}
                onChange={handleChange}
              />
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="demoUrl">Demo URL</FormLabel>
              <Input
                id="demoUrl"
                type="text"
                name="demoUrl"
                placeholder="Enter project demo URL"
                value={formData.demoUrl}
                onChange={handleChange}
              />
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="image">Project Image</FormLabel>
              <FileInput
                id="image"
                type="file"
                accept="image/*"
                onChange={handleFileChange}
              />
            </FormGroup>
            <Button type="submit" disabled={uploading}>
              {uploading ? 'Uploading...' : 'Add Project'}
            </Button>
          </Form>
        </FormWrapper>
        
      </ContentWrapper>
    </Container>
  );
};

export default Projects; 